import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import CheckIcon from "@mui/icons-material/Check";
import classNames from "classnames";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import PiChip, { PiChipProps } from "./PiChip";
import { getPortalZoom } from "../helpers/screenScale";
import { usePiColorScope } from "./PiColorScope";

export interface PiChipSelectOption {
  id: string;
  label: string;
  /** Leading visual, e.g. an ActorAvatar or PropAvatar at size 28. */
  icon?: React.ReactNode;
  /** Options sharing a group are listed under that heading, in first-seen order. */
  group?: string;
  /** One quiet line under the label in the menu, for options that need explaining. */
  description?: string;
}

interface PiChipSelectProps {
  value: string;
  options: PiChipSelectOption[];
  onChange: (id: string) => void;
  /** Shown (dashed) when `value` matches no option. */
  placeholder: string;
  /** Icon for the placeholder state, e.g. an empty avatar. */
  placeholderIcon?: React.ReactNode;
  "aria-label"?: string;
  disabled?: boolean;
  className?: string;
  tone?: PiChipProps["tone"];
  fit?: boolean;
  /** Native tooltip on the chip. */
  title?: string;
}

/**
 * Fixed-width chip dropdown from the reworked interaction previews
 * (Design Studio `lookat-rework`): an avatar or icon, a label and a chevron.
 * Every chip is the same width so rows of them line up, whatever the names.
 *
 * The menu portals into <body>. Keep it that way: #root carries the automatic
 * screen-scale zoom (helpers/screenScale.ts), and popups positioned from inside
 * it land in the wrong place.
 */
export default function PiChipSelect(props: PiChipSelectProps) {
  const { value, options, onChange, placeholder, placeholderIcon, disabled } = props;
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const colorScope = usePiColorScope();
  const triggerRef = useRef<HTMLButtonElement>(null);
  // the menu portals out of the screen-scaling zoom, so it zooms itself to match the chip
  const zoom = open ? getPortalZoom(triggerRef.current) : 1;

  const selected = options.find((option) => option.id === value);
  const hasDescriptions = options.some((option) => option.description);

  const groups: { name?: string; options: PiChipSelectOption[] }[] = [];
  for (const option of options) {
    const group = groups.find((g) => g.name === option.group);
    if (group) group.options.push(option);
    else groups.push({ name: option.group, options: [option] });
  }

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <PiChip
          ref={triggerRef}
          aria-label={props["aria-label"]}
          disabled={disabled}
          label={selected ? selected.label : placeholder}
          icon={selected ? selected.icon : placeholderIcon}
          isPlaceholder={!selected}
          isOpen={open}
          tone={props.tone}
          fit={props.fit}
          className={props.className}
          title={props.title}
        />
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={6}
          collisionPadding={8}
          className={classNames(
            "z-[1500] flex flex-col max-w-[300px] overflow-y-auto pit-scrollbar",
            // described options are two or three lines each: let a short list show in full
            hasDescriptions ? "max-h-[420px]" : "max-h-[240px]",
            "py-1.5 rounded-[6px] border border-solid border-creator-border bg-creator-surface",
            "shadow-[0_4px_12px_rgba(16,24,40,0.08)] outline-none",
            colorScope
          )}
          // the trigger width is measured on screen, so it is divided back into this zoom
          style={{ zoom, minWidth: `calc(var(--radix-dropdown-menu-trigger-width) / ${zoom})` }}
        >
          {options.length === 0 && (
            <div className="px-3 py-2 text-[13px] text-creator-text-faded">
              {String(t("chip_select.no_options"))}
            </div>
          )}
          {groups.map((group, groupIndex) => (
            <DropdownMenu.Group key={group.name ?? `group-${groupIndex}`}>
              {groupIndex > 0 && (
                <DropdownMenu.Separator className="h-px mx-2 my-1 bg-creator-border" />
              )}
              {group.name && (
                <DropdownMenu.Label className="px-3 py-1 text-[12px] font-medium text-creator-text-sub">
                  {group.name}
                </DropdownMenu.Label>
              )}
              {group.options.map((option) => {
                const isSelected = option.id === value;
                return (
                  <DropdownMenu.Item
                    key={option.id}
                    onSelect={() => onChange(option.id)}
                    className={classNames(
                      "flex items-center gap-3 min-h-[36px] px-3 text-[14px] cursor-pointer outline-none select-none",
                      option.description ? "py-2" : "py-1",
                      "data-[highlighted]:bg-interaction-primary-washed",
                      {
                        "text-interaction-primary font-semibold bg-interaction-primary-selected dark:bg-[#2f4250]":
                          isSelected,
                        "text-creator-text": !isSelected,
                      }
                    )}
                  >
                    {option.icon}
                    {option.description ? (
                      <span className="flex min-w-0 flex-col gap-[2px]">
                        <span className="truncate">{option.label}</span>
                        <span className="whitespace-normal text-[12px] font-normal leading-[1.35] text-creator-text-sub">
                          {option.description}
                        </span>
                      </span>
                    ) : (
                      <span className="min-w-0 truncate">{option.label}</span>
                    )}
                    {isSelected && (
                      <CheckIcon className="ml-auto flex-none" sx={{ fontSize: 16 }} />
                    )}
                  </DropdownMenu.Item>
                );
              })}
            </DropdownMenu.Group>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
